import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';

export type SortDirection = 'none' | 'asc' | 'desc';

export interface SortEvent {
  field: string;
  direction: SortDirection;
}

export interface TableSortColumn {
  label: string;
  field: string;
  type?: 'string' | 'number' | 'date';
  cssClass?: string;
}

@Component({
    selector: 'table-column-sort',
    templateUrl: './table-filter.component.html',
    styleUrls: ['./table-filter.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TableColumnSortComponent<T = unknown> implements OnChanges {
  @Input() label: string = '';
  @Input() field: string = '';
  @Input() direction: SortDirection = 'none';
  @Output() sortChange = new EventEmitter<SortEvent>();

  @Input() columns: TableSortColumn[] = [];
  @Input() data: T[] = [];
  @Output() sortedData = new EventEmitter<T[]>();

  sortField: string = '';
  sortDirection: SortDirection = 'none';

  get isTableMode(): boolean {
    return this.columns.length > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.isTableMode) {
      this.emitSortedData();
    }
  }

  toggleSort(): void {
    if (this.direction === 'none') {
      this.direction = 'asc';
    } else if (this.direction === 'asc') {
      this.direction = 'desc';
    } else {
      this.direction = 'none';
    }

    this.sortChange.emit({
      field: this.field,
      direction: this.direction
    });
  }

  onColumnSort(column: TableSortColumn): void {
    if (this.sortField !== column.field) {
      this.sortField = column.field;
      this.sortDirection = 'asc';
    } else if (this.sortDirection === 'none') {
      this.sortDirection = 'asc';
    } else if (this.sortDirection === 'asc') {
      this.sortDirection = 'desc';
    } else {
      this.sortDirection = 'none';
      this.sortField = '';
    }
    this.emitSortedData();
  }

  getDirection(field: string): SortDirection {
    return this.sortField === field ? this.sortDirection : 'none';
  }

  private emitSortedData(): void {
    if (!this.data || this.data.length === 0) {
      this.sortedData.emit([]);
      return;
    }

    if (this.sortDirection === 'none' || !this.sortField) {
      this.sortedData.emit([...this.data]);
      return;
    }

    const column = this.columns.find(c => c.field === this.sortField);
    const sorted = [...this.data].sort((a, b) => {
      const aVal = (a as Record<string, unknown>)[this.sortField];
      const bVal = (b as Record<string, unknown>)[this.sortField];

      if (column?.type === 'date') {
        const aDate = new Date(aVal as string).getTime();
        const bDate = new Date(bVal as string).getTime();
        return this.sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }

      if (column?.type === 'number' || typeof aVal === 'number') {
        const aNum = Number(aVal) || 0;
        const bNum = Number(bVal) || 0;
        return this.sortDirection === 'asc' ? aNum - bNum : bNum - aNum;
      }

      const aStr = String(aVal || '');
      const bStr = String(bVal || '');
      const comparison = aStr.localeCompare(bStr, 'pt-BR', { sensitivity: 'base' });
      return this.sortDirection === 'asc' ? comparison : -comparison;
    });

    this.sortedData.emit(sorted);
  }
}